import { FailedResponse } from '../responses/response'
import * as Record from 'fp-ts/Record'
import { ArrayDiff, Bool, EmptyObject, ID } from '../types/UtilityTypes'
import * as A from 'fp-ts/Array'
import { identity, not } from 'fp-ts/function'
import { Eq } from 'fp-ts/Eq'
import { fromCompare, Ord, ordString } from 'fp-ts/Ord'
import * as N from 'fp-ts/NonEmptyArray'
import { none, Option, some } from 'fp-ts/Option'
import { pipe } from 'fp-ts/pipeable'
import { isHtmlElement } from './type-guards'
import { shallowEqual } from 'react-redux'

export const getResponseErrors = (res: FailedResponse, defaultMessage?: string): string => {
    const fieldErrors = Record.toArray(res.field_errors || {}).map(([, messages]) => messages).join('\n')
    return res.error || fieldErrors || defaultMessage || 'Неизвестная ошибка'
}

export const padDeclination = (values: {
    '1': string, // 1 задача
    '2, 3, 4': string, // 2 задачи
    '0, 5 - 10': string, // 0 задач
}) => (number: number): string => {
    const newNumber: number = number % 20
    let resultString = values['0, 5 - 10']
    if (newNumber === 1) {
        resultString = values['1']
    }
    if ((newNumber >= 2) && (newNumber <= 4)) {
        resultString = values['2, 3, 4']
    }
    return '' + number + ' ' + resultString
}

export const taskDeclination = padDeclination({
    '1': 'Задача',
    '2, 3, 4': 'Задачи',
    '0, 5 - 10': 'Задач'
})

export const requestedDeclination = padDeclination({
    '1': 'не распределена',
    '2, 3, 4': 'не распределены',
    '0, 5 - 10': 'не распределено',
})

export const closedDeclination = padDeclination({
    '1': 'закрыта',
    '2, 3, 4': 'закрыто',
    '0, 5 - 10': 'закрыто',
})

export const lineDeclination = padDeclination({
    '1': 'линии',
    '2, 3, 4': 'линий',
    '0, 5 - 10': 'линий',
})

export const objectDeclination = padDeclination({
    '1': 'объект',
    '2, 3, 4': 'объекта',
    '0, 5 - 10': 'объектов'
})

export const projectDeclination = padDeclination({
    '1': 'Проект',
    '2, 3, 4': 'Проекта',
    '0, 5 - 10': 'Проектов',
})

export function unit<T>(object?: T | null): T[] {
    return object ? [object] : []
}

export function nullUnit<T>(object?: T | null): (T | null)[] {
    return object !== undefined ? [object]: []
}

export const unbox = <T>(array: T[]): T[] | undefined => A.isEmpty(array) ? undefined : array

export const isDefinedRecord = <T extends EmptyObject>(record: T): boolean => !Record.isEmpty(record as Record<string, unknown>) && Object.values(record).some(isDefined)

export const unboxRecord = <T extends Record<string, unknown>>(object: T): T | undefined => {
    if (isDefinedRecord(object)) {
        return object
    }
}

export const snoc = <T>(value: T) => (array: T[] = []): T[] => [...array, value]

export const concat = <T>(...tails: T[][]) => (head: T[]): T[] => head.concat(...tails)

export const isDefined = <T>(value: T | undefined | null): value is T => !!value

export const isDefinedArray = <T>(arr: readonly (T | undefined)[]): arr is T[] => arr.every(isDefined)

export const isDefinedTuple = <T1, T2>(tuple: [T1 | undefined, T2 | undefined]): tuple is [T1, T2] =>
    isDefined(tuple[0]) && isDefined(tuple[1])

export const isDefinedFirst = <T1, T2>(tuple: [T1 | undefined, T2 | undefined]): tuple is [T1, T2 | undefined] =>
    isDefined(tuple[0])

export const mergeObject = <T extends EmptyObject>(changes: Partial<T>) => (source: T): T => {
    return {
        ...source,
        ...changes,
    }
}

export const fromBool = (value: Bool): boolean => value === 1
export const toBool = (value: boolean): Bool => value ? 1 : 0
export const toggleBool = (value: Bool): Bool => value ? 0 : 1

export function* getIdGenerator(): Generator<number, number, number> {
    for (let i = 0; true; i++) {
        yield i
    }
}

export const splitFileName = (filename: string): [string, string] => {
    const index = filename.lastIndexOf('.')
    if (index >= 0) {
        return [filename.substr(0, index), filename.substr(index + 1)]
    } else {
        return [filename, '']
    }
}

export const setProp = <T extends EmptyObject, K extends keyof T>(key: K, value: T[K]) => (object: T): T => ({
    ...object,
    [key]: value,
})

export const buildMergeCallback = <T extends EmptyObject, K extends keyof T>(type: T, key: K, callback: (type: T) => void) => (value: T[K]): void =>
    callback({ ...type, [key]: value })

export const modifyProp = <T extends EmptyObject, K extends keyof T>(key: K, modifier: (value: T[K]) => T[K]) => (object: T): T => ({
    ...object,
    [key]: modifier(object[key]),
})

export const pick = <T extends EmptyObject, K extends keyof T>(object: T, ...keys: K[]): Pick<T, K> => {
    const result: Partial<T> = {}
    keys.forEach(key => {
        result[key] = object[key]
    })
    return result as Pick<T, K>
}

export const omit = <K extends string>(...keys: K[]) => <T extends { [key in K]: unknown }>(object: T): Omit<T, K> => {
    const result: T = { ...object }
    keys.forEach(key => {
        delete result[key]
    })
    return result
}

export const defaultValue = <T>(value: T) => (nullable: T | undefined | null): T => nullable ?? value
export const defaultValueW = <A>(value: A) => <B>(nullable: B | undefined | null): A | B => nullable ?? value

// eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/explicit-module-boundary-types
export const ignore = (value: unknown) => identity

export function catchDefault<T>(def: T) {
    return (func: () => T): T => {
        try {
            return func()
        } catch (e) {
            console.warn(e)
            return def
        }
    }
}

export function* createIdGenerator(): Generator<ID, ID> {
    for (let i = -1; true; i--) {
        yield i
    }
}

export function nonNullable<T>(value: T | null): value is T {
    return value !== null
}

export const replace = <T>(eq: Eq<T>) => (old: T, value: T) => (array: T[]): T[] =>
    array.map(item => eq.equals(item, old) ? value : item)

export const eqPredicate = <T>(eq: Eq<T>) => (value: T) => (item: T): boolean => eq.equals(value, item)

export const toggle = <T>(eq: Eq<T>) => (value: T) => (array: T[]): T[] => {
    const exist = A.elem(eq)(value, array)
    return (exist ? A.filter(not(eqPredicate(eq)(value))) : snoc(value))(array)
}

export const conditionSnoc = <T>(condition: boolean, value: T): (array: T[]) => T[] => (condition ? snoc : ignore)(value)
export const conditionCons = <T>(condition: boolean, value: T): (array: T[]) => T[] => (condition ? A.cons : ignore)(value)

export const swap = <First, Second, Result>(fun: (first: First) => (second: Second) => Result) => (second: Second) => (first: First): Result => fun(first)(second)

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = (): void => {
}

export const entries = <K extends string, T>(record: Record<K, T>): [K, T][] =>
    Record.collect((k: K, t: T) => [k, t] as [K, T])(record)

export const values = <K extends string, T>(record: Record<K, T>): T[] => Object.values(record)

export const max = <T>(ord: Ord<T>) => (array: T[]): Option<T> => A.isNonEmpty(array) ? some(N.max(ord)(array)) : none

export const diffArray = <T>(ord: Ord<T>) => (array: T[] = [], changes: T[] = []): ArrayDiff<T> => {
    const sort = A.sort(ord)
    const sortedArray = sort(array)
    const sortedChanges = sort(changes)
    pipe(A.difference(ord)(sortedChanges, sortedArray))
    return {
        add: A.difference(ord)(sortedChanges, sortedArray),
        delete: A.difference(ord)(sortedArray, sortedChanges),
    }
}

export const getOrdOpt = <T>(ord: Ord<T>): Ord<T | undefined> => fromCompare<T | undefined>((a, b) => {
    if (isDefined(a) && isDefined(b)) {
        return ord.compare(a, b)
    }
    if (isDefined(a) && !isDefined(b)) {
        return 1
    }
    if (!isDefined(a) && isDefined(b)) {
        return -1
    }
    return 0
})

export const ordStringOpt = getOrdOpt(ordString)

export const isPathIncludes = (event: Event, className: string): boolean => !!event.composedPath().filter(isHtmlElement).find(event => event.className.includes(className))

export const shallowSet = <S, C>(changes: C) => (state: S): S | C => shallowEqual(state, changes)? state: changes

export const xor = (a: boolean, b: boolean): boolean => a !== b

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
}

export const conditionReverse = (condition: boolean): <T>(as: T[]) => T[] => condition? A.reverse: identity

